import { isNil } from 'lodash/fp';
import { useCallback } from 'react';
import { type ColorBlockSpecsFragment } from '../../../../api/fragments/ColorBlockSpecs';
import { type MatrixSpecsFragment } from '../../../../api/fragments/MatrixSpecs';
import { useCreateColorBlockMutation } from '../../../../api/mutations/createColorBlock';
import { useUpdateColorBlockMutation } from '../../../../api/mutations/updateColorBlock';
import { BlockType } from '../../../../api/types';
import useHandleColorMatrixAsset from './useHandleColorMatrixAsset';
import useHandleColorSettingAsset from './useHandleColorSettingAsset';
import { type ColorBlockFormValues, type ColorBlockInput, type ColorSettingFormValues } from './utils';

const useHandleBlockSave = () => {
    const [createColorBlockMutation] = useCreateColorBlockMutation();
    const [updateColorBlockMutation] = useUpdateColorBlockMutation();

    const { uploadColorSettingAsset, deleteColorSettingAsset } = useHandleColorSettingAsset();
    const handleColorMatrixAsset = useHandleColorMatrixAsset();

    const handleColorMatrix = useCallback(
        async (
            variantConfiguratorId: string,
            colorBlock: ColorBlockSpecsFragment,
            matrix: MatrixSpecsFragment[],
            colorSettings: ColorSettingFormValues[]
        ) => {
            const colorTrimCombo = matrix.reduce((accumulator, currentValue) => {
                const previousColorAccumulator = { ...accumulator[currentValue.colorId] };

                return {
                    ...accumulator,
                    [currentValue.colorId]: {
                        ...previousColorAccumulator,
                        [currentValue.trimId]: currentValue.variantColorImage,
                    },
                };
            }, {});

            // begin upload section images, which is matrix
            const colorMatrixAsset = colorSettings?.flatMap(({ sectionImages, id }, index) => {
                const colorId = id ?? colorBlock?.colorSettings[index]?.id;

                return (sectionImages || []).map(sectionImage => {
                    if (!sectionImage.trimId) {
                        return Promise.resolve();
                    }

                    const initialVariantColorImage = colorTrimCombo?.[colorId]?.[sectionImage.trimId];

                    return handleColorMatrixAsset(
                        variantConfiguratorId,
                        colorId,
                        sectionImage.trimId,
                        sectionImage.variantColorImage,
                        initialVariantColorImage
                    );
                });
            });

            if (colorMatrixAsset.length) {
                await Promise.all(colorMatrixAsset);
            }
        },
        [handleColorMatrixAsset]
    );

    const createColorBlock = useCallback(
        async (variantConfiguratorId: string, colorBlockInput: ColorBlockInput) => {
            const { isActive, sectionTitle, colorSettings } = colorBlockInput;

            const { data } = await createColorBlockMutation({
                variables: {
                    settings: {
                        type: BlockType.Color,
                        colorBlockInput: {
                            isActive,
                            sectionTitle,
                            colorSettings: colorSettings.map(({ image, sectionImages, ...rest }) => rest),
                        },
                    },
                    variantConfiguratorId,
                },
            });

            const colorBlock = data?.variantConfigurator?.colorBlock;

            // after settings is created, upload the image
            const colorSettingAssetToUpload = colorSettings?.map((setting, index) => {
                // can use index because the creation using same input
                const foundSetting = colorBlock?.colorSettings[index];

                if (setting.image instanceof File) {
                    return uploadColorSettingAsset(
                        variantConfiguratorId,
                        colorBlock?.id,
                        foundSetting?.id,
                        setting.image
                    );
                }

                return Promise.resolve();
            });

            if (colorSettingAssetToUpload.length) {
                await Promise.all(colorSettingAssetToUpload);
            }

            await handleColorMatrix(
                variantConfiguratorId,
                colorBlock,
                data?.variantConfigurator?.matrix,
                colorSettings
            );
        },
        [createColorBlockMutation, handleColorMatrix, uploadColorSettingAsset]
    );

    const updateColorBlock = useCallback(
        async (
            variantConfiguratorId: string,
            blockId: string,
            colorBlockInput: ColorBlockInput,
            initialBlock: ColorBlockSpecsFragment
        ) => {
            const { isActive, sectionTitle, colorSettings } = colorBlockInput;

            // delete asset first
            // detect from initial block first
            const colorSettingAssetsToDelete = initialBlock?.colorSettings
                .filter(initialSetting => {
                    const foundSetting = colorSettings.find(setting => setting?.id && setting.id === initialSetting.id);

                    return isNil(foundSetting?.image);
                })
                .map(setting => deleteColorSettingAsset(variantConfiguratorId, blockId, setting.id));

            if (colorSettingAssetsToDelete.length) {
                await Promise.all(colorSettingAssetsToDelete);
            }

            // begin update color block
            const { data } = await updateColorBlockMutation({
                variables: {
                    settings: {
                        type: BlockType.Color,
                        colorBlockInput: {
                            isActive,
                            sectionTitle,
                            colorSettings: colorSettings.map(({ image, sectionImages, ...rest }) => rest),
                        },
                    },
                    variantConfiguratorId,
                    blockId,
                },
            });

            const colorBlock = data?.variantConfigurator?.colorBlock;

            // upload color setting assets
            const colorSettingAssetsToUpload = colorSettings?.map((setting, index) => {
                if (setting.image instanceof File) {
                    // It should be the same index as the input
                    const settingId = setting.id ?? colorBlock?.colorSettings[index]?.id;

                    return uploadColorSettingAsset(variantConfiguratorId, blockId, settingId, setting.image);
                }

                return Promise.resolve();
            });

            if (colorSettingAssetsToUpload.length) {
                await Promise.all(colorSettingAssetsToUpload);
            }

            await handleColorMatrix(
                variantConfiguratorId,
                colorBlock,
                data?.variantConfigurator?.matrix,
                colorSettings
            );
        },
        [deleteColorSettingAsset, handleColorMatrix, updateColorBlockMutation, uploadColorSettingAsset]
    );

    return useCallback(
        async (values: ColorBlockFormValues, variantConfiguratorId: string, initialBlock: ColorBlockSpecsFragment) => {
            const { colorBlockInput } = values;
            const { id, ...settings } = colorBlockInput;

            if (id) {
                await updateColorBlock(variantConfiguratorId, id, settings, initialBlock);
            } else {
                await createColorBlock(variantConfiguratorId, settings);
            }
        },
        [createColorBlock, updateColorBlock]
    );
};

export default useHandleBlockSave;
